import { session } from '../../utilities/session';
import { Space, Button, Typography, Form, Input, message } from 'antd';
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import Logo from '../../components/logo/Logo'

import "./Login.css"

import LoginBranding from "./Branding";
import Texty from 'rc-texty';
import "./Login.less"
import useWindowDimensions from '../../utilities/window_size';

const AuthView = ({ type }) => {
  const [isRegistering, setIsRegistering] = useState(type === 'register');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { width } = useWindowDimensions();
  const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  const inviteToken = searchParams.get('inviteToken') || '';

  useEffect(() => {
    setIsRegistering(type === 'register');
    if (type === 'logout') session.clear();
  }, [type]);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      console.log('Attempting login with:', { email: values.email });
      
      const response = await fetch(`${process.env.REACT_APP_AUTH_URL}/realms/${process.env.REACT_APP_Keycloak_Realm}/protocol/openid-connect/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'password',
          client_id: 'fieldops-frontend',
          username: values.email,
          password: values.password,
          scope: 'openid profile email',
        }),
      });

      console.log('Login response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Login successful, token received:', !!data.access_token);
        session.install(data);

        message.success('Login successful!');
        console.log('Navigating to home');
        navigate('/home');
        console.log('Navigation completed');
      } else {
        const errorData = await response.json();
        console.error('Login failed:', response.status, errorData);

        if (response.status === 401) {
          message.error('Invalid email or password');
        } else {
          message.error(`Login failed: ${errorData.error_description || errorData.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleRegister = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      if (!apiUrl) {
        throw new Error('Missing API URL');
      }

      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          ...(inviteToken ? { inviteToken } : {}),
        }),
      });

      if (response.ok) {
        if (inviteToken) {
          message.success('Registration successful! Please login to continue.');
          form.resetFields();
          navigate('/login');
        } else {
          message.success('Registration successful! Please check your email for verification.');
          setIsRegistering(false);
          form.resetFields();
        }
      } else {
        let errorMessage = 'Registration failed. Please try again.';
        try {
          const errorData = await response.json();
          if (response.status === 409) {
            errorMessage = 'User already exists';
          } else if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          if (response.status === 409) {
            errorMessage = 'User already exists';
          }
        }
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Registration error:', error);
      message.error('Registration failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <Space direction='horizontal' style={{margin: "auto", width: "100%", justifyContent: "space-evenly"}}>
      {/* Branding side of the login page */}
      {width > 1300 && <LoginBranding />}

      {/* Auth form side */}
      <Space direction='vertical' style={{padding: 20, maxWidth: 400, width: '100%'}}>
        <Space direction='horizontal' style={{ margin: "auto", display: "flex", width: "100%", justifyContent: 'center' }}>
          <div style={{width: 35}}>
            <Logo brandmark={true} />
          </div>
          <Typography.Title style={{fontSize: 35, whiteSpace: "nowrap"}} type='secondary'>
            <Texty>
              Agcurate FieldOps™
            </Texty>
          </Typography.Title>
        </Space>

        <Typography.Title level={3} style={{textAlign: 'center', marginBottom: 30}}>
          {isRegistering ? 'Create Account' : 'Welcome Back'}
        </Typography.Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={isRegistering ? handleRegister : handleLogin}
          requiredMark={false}
        >
          {isRegistering && (
            <>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter your first name' }]}
              >
                <Input size="large" placeholder="Enter your first name" />
              </Form.Item>

              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter your last name' }]}
              >
                <Input size="large" placeholder="Enter your last name" />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input size="large" placeholder="Enter your email" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password size="large" placeholder="Enter your password" />
          </Form.Item>

          {isRegistering && (
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password size="large" placeholder="Confirm your password" />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="agci-button"
              style={{width: '100%'}}
            >
              {isRegistering ? 'Create Account' : 'Login'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{textAlign: 'center'}}>
          <Button
            type="link"
            onClick={() => {
              setIsRegistering(!isRegistering);
              form.resetFields();
            }}
          >
            {isRegistering ? 'Already have an account? Login' : "Don't have an account? Register"}
          </Button>
        </div>
      </Space>
    </Space>
  );
};

export default AuthView;
